import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import api from '@/utils/apiClient';

/**
 * MenuDisplayBoard - Full menu display for window tablets
 * Shows all menu categories with items sorted by price (high to low)
 * Auto-scrolls continuously with sync across all 3 displays
 * 
 * Designed for widescreen tablets in horizontal/landscape orientation
 * Single column layout with large items for storefront visibility
 */
const MenuDisplayBoard = () => {
  const { displayNumber: paramDisplayNumber } = useParams();
  const location = useLocation();
  
  // Extract display number from URL
  const getDisplayNumber = () => {
    if (paramDisplayNumber) return parseInt(paramDisplayNumber);
    const match = location.pathname.match(/display(\d+)/);
    if (match) return parseInt(match[1]);
    return 1;
  };
  
  const displayNum = getDisplayNumber();
  
  // State
  const [menuData, setMenuData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  
  // Refs
  const scrollContainerRef = useRef(null);
  const contentRef = useRef(null);
  const scrollAnimationRef = useRef(null);
  const syncIntervalRef = useRef(null);
  
  // Scroll speed (pixels per second) - slightly faster
  const SCROLL_SPEED = 45;
  
  // Fetch menu data
  const fetchMenuData = useCallback(async () => {
    try {
      const response = await api.get('/display-menu/full-menu');
      const data = response.data;
      
      if (data.success) {
        setMenuData(data.menu || []);
        setError(null);
      }
    } catch (err) {
      console.error('Failed to fetch menu:', err);
      setError('Failed to load menu');
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  // Initial fetch and periodic refresh
  useEffect(() => {
    fetchMenuData();
    
    // Refresh menu data every 60 seconds to catch updates
    const refreshInterval = setInterval(fetchMenuData, 60000);
    
    return () => clearInterval(refreshInterval);
  }, [fetchMenuData]);
  
  // Sync scroll position across displays
  const syncScrollPosition = useCallback(async () => {
    try {
      const response = await api.get('/display-menu/sync-status');
      const data = response.data;
      
      if (contentRef.current && scrollContainerRef.current) {
        const contentHeight = contentRef.current.scrollHeight;
        const containerHeight = scrollContainerRef.current.clientHeight;
        const scrollableHeight = contentHeight - containerHeight;
        
        if (scrollableHeight > 0) {
          const totalSecondsInCycle = 3600;
          const currentSeconds = data.total_seconds_in_hour || 0;
          const scrollDuration = scrollableHeight / SCROLL_SPEED;
          const cycleDuration = scrollDuration + 10;
          const positionInCycle = currentSeconds % cycleDuration;
          
          if (positionInCycle < scrollDuration) {
            const targetPosition = (positionInCycle / scrollDuration) * scrollableHeight;
            setScrollPosition(targetPosition);
          } else {
            setScrollPosition(0);
          }
        }
      }
    } catch (err) {
      console.error('Sync failed:', err);
    }
  }, []);
  
  // Start continuous scroll animation
  useEffect(() => {
    if (isLoading || menuData.length === 0) return;
    
    // Initial sync
    syncScrollPosition();
    
    // Sync every 10 seconds to stay aligned
    syncIntervalRef.current = setInterval(syncScrollPosition, 10000);
    
    // Smooth scroll animation
    let lastTime = performance.now();
    
    const animate = (currentTime) => {
      if (!contentRef.current || !scrollContainerRef.current) {
        scrollAnimationRef.current = requestAnimationFrame(animate);
        return;
      }
      
      const deltaTime = (currentTime - lastTime) / 1000;
      lastTime = currentTime;
      
      const contentHeight = contentRef.current.scrollHeight;
      const containerHeight = scrollContainerRef.current.clientHeight;
      const scrollableHeight = contentHeight - containerHeight;
      
      if (scrollableHeight > 0) {
        setScrollPosition(prev => {
          const newPosition = prev + (SCROLL_SPEED * deltaTime);
          if (newPosition >= scrollableHeight) {
            return 0;
          }
          return newPosition;
        });
      }
      
      scrollAnimationRef.current = requestAnimationFrame(animate);
    };
    
    scrollAnimationRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (scrollAnimationRef.current) {
        cancelAnimationFrame(scrollAnimationRef.current);
      }
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
    };
  }, [isLoading, menuData, syncScrollPosition]);
  
  // Apply scroll position
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.style.transform = `translateY(-${scrollPosition}px)`;
    }
  }, [scrollPosition]);
  
  // Loading state
  if (isLoading) {
    return (
      <div className="h-screen w-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-24 h-24 border-4 border-gray-300 border-t-amber-600 rounded-full animate-spin mx-auto mb-8"></div>
          <p className="text-gray-800 text-4xl font-light tracking-widest">Loading Menu...</p>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="h-screen w-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-4xl mb-6">{error}</p>
          <button 
            onClick={fetchMenuData}
            className="px-8 py-4 bg-red-600 text-white text-2xl rounded-xl hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
  
  // Empty menu state
  if (menuData.length === 0) {
    return (
      <div className="h-screen w-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-9xl mb-8">☕</div>
          <p className="text-gray-800 text-4xl tracking-widest">Menu Coming Soon</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="h-screen w-screen bg-white overflow-hidden relative">
      {/* Scrolling content container - no header */}
      <div 
        ref={scrollContainerRef}
        className="h-full w-full overflow-hidden"
      >
        <div 
          ref={contentRef}
          className="will-change-transform"
          style={{ transition: 'transform 0.1s linear' }}
        >
          {/* Menu content - single column, large items */}
          <div className="px-8 py-6">
            {menuData.map((category, catIndex) => (
              <div key={category.category_id} className="mb-8">
                {/* Category Header - Large and prominent */}
                <div className="mb-6 py-4 border-b-4 border-amber-600">
                  <h2 className="text-6xl font-black text-gray-900 tracking-wider uppercase text-center">
                    {category.category_name}
                  </h2>
                </div>
                
                {/* Items - Single column, very large cards - only 2 visible at a time */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  {category.items.map((item, itemIndex) => (
                    <LargeMenuItemCard key={item.id || itemIndex} item={item} />
                  ))}
                </div>
              </div>
            ))}
            
            {/* Bottom spacer for smooth looping */}
            <div className="h-48" />
          </div>
        </div>
      </div>
      
      {/* Subtle top fade - light version */}
      <div className="absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-white/80 to-transparent pointer-events-none z-10" />
      
      {/* Bottom fade - light version */}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent pointer-events-none z-10" />
    </div>
  );
};


/**
 * Large menu item card - single column layout
 * Image on left, name/description in middle, large price on right
 * Card height is 380px to ensure only ~2 items visible on 800px height screen
 * Light theme for daylight visibility
 */
const LargeMenuItemCard = ({ item }) => {
  const [imageError, setImageError] = useState(false);
  
  const hasImage = item.image_url && !imageError;
  
  return (
    <div 
      className="bg-gray-50 rounded-3xl overflow-hidden border-2 border-gray-200 flex items-stretch shadow-lg"
    >
      {/* Image section - large square */}
      <div 
        className="flex-shrink-0 relative bg-gray-100"
        style={{ width: '380px', height: '380px' }}
      >
        {hasImage ? (
          <img
            src={item.image_url}
            alt={item.name}
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100">
            <span style={{ fontSize: '160px' }} className="opacity-30">☕</span>
          </div>
        )}
      </div>
      
      {/* Text content - name and description - black text for visibility */}
      <div className="flex-1 px-12 py-10 flex flex-col justify-center overflow-hidden" style={{ minHeight: '380px' }}>
        <h3 className="text-gray-900 font-black leading-tight mb-5 line-clamp-2" style={{ fontSize: '56px' }}>
          {item.name}
        </h3>
        {item.description && (
          <p className="text-gray-600 leading-relaxed line-clamp-3" style={{ fontSize: '32px' }}>
            {item.description}
          </p>
        )}
      </div>
      
      {/* Price - clean black text, no background */}
      <div className="flex-shrink-0 pr-10 flex items-center">
        <div 
          className="text-gray-900 font-black whitespace-nowrap"
          style={{ fontSize: '80px' }}
        >
          ${item.price?.toFixed(0) || '0'}
        </div>
      </div>
    </div>
  );
};

export default MenuDisplayBoard;
